'use client';

import { useTranslations } from 'next-intl';
import { Icons } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';

type MoodKey = 'like' | 'dislike' | 'happy' | 'sad' | 'laughing' | 'awkward' | 'weary';

interface Mood {
  key: MoodKey;
  icon: (typeof Icons)[keyof typeof Icons];
  // Search term shared by both tabs: Giphy query (GIF) and emoji keyword search.
  term: string;
}

const MOODS: Mood[] = [
  { key: 'like', icon: Icons.EmojiThumbsUp, term: 'thumbs up' },
  { key: 'dislike', icon: Icons.EmojiThumbsDown, term: 'thumbs down' },
  { key: 'happy', icon: Icons.EmojiSmile, term: 'happy' },
  { key: 'sad', icon: Icons.EmojiSad, term: 'sad' },
  { key: 'laughing', icon: Icons.EmojiTearsOfJoy, term: 'laughing' },
  { key: 'awkward', icon: Icons.EmojiSadSweat, term: 'awkward' },
  { key: 'weary', icon: Icons.EmojiWeary, term: 'weary' },
];

interface MoodBarProps {
  // Term of the currently selected mood (highlighted), or null when none.
  activeTerm?: string | null;
  onSelect: (term: string) => void;
}

// Quick "mood" shortcuts shown inside the search bar: tap one to instantly
// filter by that mood (GIFs or emoji). The picked mood stays highlighted — the
// search text isn't touched — like category shortcuts in other messengers.
export const MoodBar = ({ activeTerm, onSelect }: MoodBarProps) => {
  const t = useTranslations('gifPicker');
  return (
    <div className="flex min-w-0 items-center gap-1 overflow-hidden">
      {MOODS.map(({ key, icon: Icon, term }) => {
        const active = activeTerm === term;
        return (
          <button
            key={key}
            type="button"
            aria-label={t(`moods.${key}`)}
            aria-pressed={active}
            onClick={() => onSelect(term)}
            className={cn(
              'flex shrink-0 cursor-pointer items-center justify-center rounded-[10px] p-1 transition-colors',
              active
                ? 'bg-v2-btn-secondary-hover text-v2-text-primary'
                : 'text-v2-text-secondary hover:text-v2-text-primary',
            )}
          >
            <Icon className="size-5" />
          </button>
        );
      })}
    </div>
  );
};
